import React from 'react';
import { Campaign } from '../../types/Campaign';
import { Badge } from '../atoms/Badge';
import { ProgressBar } from '../atoms/ProgressBar';

interface CampaignCardMobileProps {
  campaign: Campaign;
}

export const CampaignCardMobile: React.FC<CampaignCardMobileProps> = ({ campaign }) => {
  const budgetPercentage = (campaign.consumedBudget / campaign.totalBudget) * 100;
  const rejectionPercentage = campaign.totalPosts > 0
    ? (campaign.totalRejectedPosts / campaign.totalPosts) * 100
    : 0;

  const getRejectionStatusColor = (percentage: number) => {
    if (percentage < 5) return 'text-emerald-600 dark:text-emerald-400';
    if (percentage <= 10) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const handleClick = () => {
    window.open(campaign.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white dark:bg-gray-800 rounded-xl p-4 hover:shadow-lg transition-all duration-200 cursor-pointer border border-gray-100 dark:border-gray-700 ${
        campaign.status === 'done' ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 mr-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-2">
            {campaign.campaignTitle}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-gray-500 dark:text-gray-400">{campaign.brandName}</p>
            {campaign.isRecurrent && (
              <span className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full whitespace-nowrap font-medium">
                Recorrente
              </span>
            )}
          </div>
        </div>
        <Badge status={campaign.status} />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Início</span>
          <span className="text-gray-700 dark:text-gray-300 font-medium">{formatDate(campaign.startDate)}</span>
        </div>
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Fim</span>
          <span className="text-gray-700 dark:text-gray-300 font-medium">{formatDate(campaign.endDate)}</span>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-500 dark:text-gray-400">Orçamento</span>
          <span className="text-xs text-gray-600 dark:text-gray-400 font-semibold">
            {budgetPercentage.toFixed(0)}%
          </span>
        </div>
        <div className="text-xs text-gray-700 dark:text-gray-300 font-medium mb-2">
          {formatCurrency(campaign.consumedBudget)} / {formatCurrency(campaign.totalBudget)}
        </div>
        <ProgressBar percentage={budgetPercentage} showLabel={false} />
      </div>

      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Criadores</span>
          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
            {campaign.creatorsWithPosts}/{campaign.creatorsRegistered}
          </span>
        </div>
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Conteúdos</span>
          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{campaign.totalPosts}</span>
        </div>
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Rejeitados</span>
          <div className="text-sm font-medium">
            <span className="text-gray-700 dark:text-gray-300">{campaign.totalRejectedPosts}</span>
            <span className={`ml-1 text-xs ${getRejectionStatusColor(rejectionPercentage)}`}>
              ({rejectionPercentage.toFixed(0)}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
